import styles from './LiveSwitch.module.css';
import { Beacon } from '@/components/beacon/Beacon';
import {
  useCanMakeEventLiveLazyQuery,
  useCanMakeEventLiveQuery,
  useGoLiveWithSubscriptionMutation,
} from '@/generated/graphql';
import { useMonetizationEnabled } from '@/hooks/useMonetizationEnabled';
import { useParamsSafe } from '@/hooks/useParamsSafe';
import { cn } from '@/lib/utils';
import { createLogger } from '@/utils/createLogger';
import { ApolloError } from '@apollo/client';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { GoLiveDialog } from '../GoLiveDialog';
import { useAdminScreenThemeState } from '@/providers/AdminScreenThemeStateProvider/AdminScreenThemeStateContext';
import { LiveDialog } from '../LiveDialog';

const logger = createLogger('LiveSwitch');
const FREE_LIVE_SCREEN_LIMIT = 3;

function goLiveErrorMessage(error: unknown) {
  if (error instanceof ApolloError) {
    return error.graphQLErrors[0]?.message || error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'Uh oh! Something went wrong.';
}

export function LiveSwitch() {
  const { id: eventId } = useParamsSafe('id');
  const [isGoLiveDialogOpen, setIsGoLiveDialogOpen] = useState(false);
  const [isLiveDialogOpen, setIsLiveDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { monetizationEnabled, loading: flagsLoading } =
    useMonetizationEnabled();

  const handleGoLiveDialogOpenChange = (open: boolean) => {
    if (!open) {
      setLoading(false);
    }
    setIsGoLiveDialogOpen((prev) => !prev);
  };

  const handleLiveDialogOpenChange = (open: boolean) => {
    if (!open) {
      setLoading(false);
    }
    setIsLiveDialogOpen((prev) => !prev);
  };

  const { event } = useAdminScreenThemeState();
  const live = Boolean(event.live);

  const [canMakeEventLive] = useCanMakeEventLiveLazyQuery();
  const { data: liveCountData, refetch: refetchLiveCount } =
    useCanMakeEventLiveQuery({
      variables: { eventId },
      skip: !eventId || monetizationEnabled,
      fetchPolicy: 'cache-and-network',
    });
  const [goLiveWithSubscription] = useGoLiveWithSubscriptionMutation();

  useEffect(() => {
    if (!monetizationEnabled && eventId) {
      refetchLiveCount();
    }
  }, [event.live, eventId, monetizationEnabled, refetchLiveCount]);

  const goLiveFree = async () => {
    setLoading(true);
    try {
      const { data } = await goLiveWithSubscription({
        variables: { eventId },
      });
      if (data?.goLiveWithSubscription) {
        toast.success('Event is now live!');
        await refetchLiveCount();
      }
    } catch (error) {
      logger.error('Error going live (monetization disabled)', error);
      toast.error(goLiveErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleClickNotLive = async () => {
    if (flagsLoading) {
      return;
    }

    if (!monetizationEnabled) {
      await goLiveFree();
      return;
    }

    setLoading(true);
    const { data, error } = await canMakeEventLive({
      variables: {
        eventId,
      },
    });

    if (error || !data?.canMakeEventLive) {
      logger.error('Error checking if event can be made live', error);
      toast.error('Uh oh! Something went wrong.');
      setLoading(false);
      return;
    }

    const { withEventPass, withSubscription } = data.canMakeEventLive;

    logger.info('canMakeEventLive', { withEventPass, withSubscription });

    if (withSubscription) {
      goLiveWithSubscription({ variables: { eventId } })
        .then(({ data }) => {
          if (data?.goLiveWithSubscription) {
            toast.success('Event is now live!');
          }
        })
        .catch((error) => {
          logger.error('Error going live with subscription', error);
          toast.error('Uh oh! Something went wrong.');
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      // Open GoLiveModal
      setIsGoLiveDialogOpen(true);
    }
  };

  const handleClickLive = async () => {
    setIsLiveDialogOpen(true);
  };

  let line2Text = 'See details';
  if (!live) {
    if (loading) {
      line2Text = 'Going Live...';
    } else {
      line2Text = 'Go Live Now!';
    }
  }

  const liveCount = liveCountData?.canMakeEventLive.liveCount;

  return (
    <>
      <div className="flex items-center gap-2">
        {!monetizationEnabled && liveCount != null && (
          <span className="hidden text-xs text-muted-foreground sm:inline">
            Live screens: {liveCount}/{FREE_LIVE_SCREEN_LIMIT}
          </span>
        )}
        <div
          data-name="LIVE_SWITCH"
          className={cn(
            styles.container,
            live ? styles.live : styles.notLive,
            loading ? styles.loading : styles.notLoading,
          )}
          onClick={live ? handleClickLive : handleClickNotLive}
        >
          <Beacon
            state={live ? 'active' : 'inactive'}
            className={styles.beacon}
          />
          <div className={styles.label}>
            <span className={styles.line1}>{live ? 'Live' : 'Not Live'}</span>
            <span className={styles.line2}>{line2Text}</span>
          </div>
        </div>
      </div>
      {monetizationEnabled && (
        <GoLiveDialog
          open={isGoLiveDialogOpen}
          onOpenChange={handleGoLiveDialogOpenChange}
        />
      )}
      <LiveDialog
        open={isLiveDialogOpen}
        onOpenChange={handleLiveDialogOpenChange}
      />
    </>
  );
}
